// apps/web/src/lib/vendas/validacao-pv.ts — Fase 12, Plano 23 (PV-01, PV-02).
// Validação pura por campo do formulário do PV, do Sheet de item e do Sheet de cabeçalho.
// Cada erro fica junto do campo (FormMessage); `primeiroCampoInvalido` diz onde pôr o foco.
import { mensagemNumeroDigitado } from './numero';

export type ErrosPorCampo<C extends string> = Partial<Record<C, string>>;

export interface DadosCabecalhoPv {
  clienteId: string | null | undefined;
  /** Só validada quando informada (a edição do cabeçalho não altera a data do PV). */
  data?: string;
  numeroPedidoCliente?: string | null;
  responsavelNome?: string | null;
  condicaoComercial?: string | null;
  observacoes?: string | null;
}

export type CampoCabecalhoPv = 'clienteId' | 'data' | 'numeroPedidoCliente' | 'responsavelNome' | 'condicaoComercial' | 'observacoes';

/** Ordem visual dos campos do cabeçalho (foco no primeiro inválido). */
export const ORDEM_CABECALHO_PV: readonly CampoCabecalhoPv[] = [
  'clienteId',
  'numeroPedidoCliente',
  'data',
  'responsavelNome',
  'condicaoComercial',
  'observacoes',
];

/** Limites do `AtualizarCabecalhoPvDto`/`CriarPedidoVendaDto` — evita ida ao servidor para um 400 previsível. */
const LIMITES_CABECALHO: ReadonlyArray<{ campo: CampoCabecalhoPv; max: number }> = [
  { campo: 'numeroPedidoCliente', max: 60 },
  { campo: 'responsavelNome', max: 120 },
  { campo: 'condicaoComercial', max: 500 },
  { campo: 'observacoes', max: 2000 },
];

export function validarCabecalhoPv(dados: DadosCabecalhoPv): ErrosPorCampo<CampoCabecalhoPv> {
  const erros: ErrosPorCampo<CampoCabecalhoPv> = {};
  if (!dados.clienteId) erros.clienteId = 'Informe o cliente.';
  if (dados.data !== undefined && !dados.data) erros.data = 'Informe a data.';
  for (const { campo, max } of LIMITES_CABECALHO) {
    const valor = dados[campo as 'numeroPedidoCliente' | 'responsavelNome' | 'condicaoComercial' | 'observacoes'];
    if (typeof valor === 'string' && valor.trim().length > max) erros[campo] = `Use no máximo ${max} caracteres.`;
  }
  return erros;
}

/** Limites do `CriarPedidoVendaItemDto`/ajustes da API (quantidade: 4 casas, até 99.999.999.999; preço: 6 casas, até PRECO_MAXIMO). */
export const QUANTIDADE_MAXIMA_ITEM_PV = 99_999_999_999;
export const CASAS_QUANTIDADE_PV = 4;
export const PRECO_MAXIMO_ITEM_PV = 999_999_999;
export const CASAS_PRECO_PV = 6;

/** True quando o valor tem no máximo `casas` casas decimais (tolerante ao erro de ponto flutuante). */
export function temNoMaximoCasas(valor: number, casas: number): boolean {
  const escala = 10 ** casas;
  return Math.abs(valor * escala - Math.round(valor * escala)) < 1e-6;
}

/** Erro de uma quantidade de item (número já lido; `texto` explica o ambíguo), ou null quando vale. */
export function erroQuantidadePv(quantidade: number, texto?: string): string | null {
  if (!Number.isFinite(quantidade)) {
    return mensagemNumeroDigitado(texto ?? '', 'A quantidade deve ser um número maior que zero (ex.: 1500 ou 1,5).');
  }
  if (quantidade <= 0) return 'A quantidade deve ser maior que zero.';
  if (quantidade > QUANTIDADE_MAXIMA_ITEM_PV) return 'A quantidade informada é grande demais.';
  if (!temNoMaximoCasas(quantidade, CASAS_QUANTIDADE_PV)) return `Use no máximo ${CASAS_QUANTIDADE_PV} casas decimais na quantidade.`;
  return null;
}

/** Erro de um preço/valor unitário (Infinity e NaN recusados; teto e casas iguais ao DTO), ou null quando vale. */
export function erroPrecoPv(preco: number, texto?: string): string | null {
  if (!Number.isFinite(preco)) return mensagemNumeroDigitado(texto ?? '', 'Informe um preço válido (ex.: 1234,50).');
  if (preco < 0) return 'O preço não pode ser negativo.';
  if (preco > PRECO_MAXIMO_ITEM_PV) return 'O preço informado é alto demais.';
  if (!temNoMaximoCasas(preco, CASAS_PRECO_PV)) return `Use no máximo ${CASAS_PRECO_PV} casas decimais no preço.`;
  return null;
}

export interface DadosItemPv {
  codigoItemCliente: string;
  quantidade: number;
  /** null = sem preço digitado (vale o da tabela); NaN = texto que não é número. */
  precoUnitario: number | null;
  /** Textos digitados (opcionais): permitem explicar o número AMBÍGUO ("1.500") em vez de "inválido" genérico. */
  quantidadeTexto?: string;
  precoTexto?: string;
  /** Validadas só quando informadas. */
  descricao?: string;
  unidade?: string;
}

export type CampoItemPv = 'codigoItemCliente' | 'descricao' | 'quantidade' | 'unidade' | 'precoUnitario';

/** Ordem visual dos campos do Sheet de item. */
export const ORDEM_ITEM_PV: readonly CampoItemPv[] = ['codigoItemCliente', 'descricao', 'quantidade', 'unidade', 'precoUnitario'];

export function validarItemPv(dados: DadosItemPv): ErrosPorCampo<CampoItemPv> {
  const erros: ErrosPorCampo<CampoItemPv> = {};
  if (!dados.codigoItemCliente.trim()) erros.codigoItemCliente = 'Informe o código do item do cliente.';
  if (dados.descricao !== undefined && !dados.descricao.trim()) erros.descricao = 'Informe a descrição do item.';
  const erroQtd = erroQuantidadePv(dados.quantidade, dados.quantidadeTexto);
  if (erroQtd) erros.quantidade = erroQtd;
  if (dados.unidade !== undefined && !dados.unidade.trim()) erros.unidade = 'Informe a unidade.';
  if (dados.precoUnitario !== null) {
    const erroPreco = erroPrecoPv(dados.precoUnitario, dados.precoTexto);
    if (erroPreco) erros.precoUnitario = erroPreco;
  }
  return erros;
}

/** Primeiro campo com erro segundo a ordem visual, ou null quando nenhum campo da ordem tem erro. */
export function primeiroCampoInvalido<C extends string>(erros: ErrosPorCampo<C>, ordem: readonly C[]): C | null {
  for (const campo of ordem) {
    if (erros[campo]) return campo;
  }
  return null;
}

/** Foca o elemento do campo inválido (por id do DOM) sem depender de refs. */
export function focarCampo(idDom: string | null | undefined): void {
  if (!idDom || typeof document === 'undefined') return;
  document.getElementById(idDom)?.focus();
}
